import React, { useEffect, useRef, useState } from "react";
import { Button, TableBody, TableCell, TableHead, TableRow, Typography } from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { carbonRequest } from "./corporateCarbon/api";
import {
  CarbonAdminPage,
  CarbonDataTable,
  CarbonForm,
  CarbonFormToolbar,
  CarbonSection,
  CarbonPageAction,
  CarbonSelectInput,
  CarbonStatus,
  CarbonTableActions,
  CarbonTechnicalDetails,
} from "./corporateCarbon/CarbonAdminPage";
import { carbonContextFromSearch, friendlyCarbonError, reportingRevisionStatusLabel as reportingRevisionLabel } from "./corporateCarbon/workflow";
import CalculationProgress from "./project/components/CalculationProgress";

export const calculationStatusLabel = (status) => ({
  pending: "Waiting to start",
  claimed: "Preparing",
  running: "Calculating",
  succeeded: "Completed",
  failed: "Needs attention",
  cancelled: "Cancelled",
  superseded: "Replaced by a newer calculation",
  draft: "Draft",
  finalized: "Finalized",
}[status] || "Unknown");

export const calculationAvailability = (revision, runs = [], releaseId, algorithmVersion = '2.0.0') => {
  if (!revision || revision.status !== "finalized") {
    return {
      allowed: false,
      reason: "Finalize this reporting revision before calculating it.",
    };
  }
  if (runs.some((run) => run.status === "succeeded" && (!releaseId || (run.factorReleaseId === releaseId && run.algorithmVersion === algorithmVersion)))) {
    return {
      allowed: false,
      reason: "These reporting data have already been calculated with this factor release and algorithm. Select another published release to recalculate, or create a correction to change the declared inputs.",
    };
  }
  if (runs.some(({ status }) => ["pending", "claimed", "running"].includes(status))) {
    return {
      allowed: false,
      reason: "A calculation is already in progress for this reporting revision.",
    };
  }
  return { allowed: true, reason: "" };
};

const requestedAtLabel = (run) => run.createdAt
  ? new Date(run.createdAt).toLocaleString()
  : "Date not recorded";
const factorReasonLabel = (value) => ({
  exact_match: "Exact match",
  proxy: "Closest available factor",
  fallback: "Fallback factor",
}[value] || String(value || "Not recorded").replace(/_/g, " "));
const activeCalculationStatuses = new Set(["pending", "claimed", "running"]);

export const CarbonCalculationControl = ({ activeRun, busy, disabled }) => {
  if (activeRun) {
    return <CalculationProgress status={{
      label: calculationStatusLabel(activeRun.status),
      runStatus: activeRun.status,
      percent: activeRun.progressPercent,
    }} />;
  }
  if (busy) {
    return <CalculationProgress status={{ label: "Starting calculation", runStatus: "pending", percent: null }} />;
  }
  return <Button color="primary" disabled={disabled} type="submit" variant="contained">Calculate emissions</Button>;
};

export const CarbonCalculationRuns = ({ runs = [], releases = [] }) => (
  <CarbonSection title="Calculation history" description="Calculations for the selected reporting revision. Attempt counts processing attempts, including retries of the same calculation.">
    {runs.length === 0 ? (
      <Typography color="textSecondary">No calculations have been requested for this reporting revision.</Typography>
    ) : (
      <CarbonDataTable label="Calculation run history">
        <TableHead><TableRow><TableCell>Requested</TableCell><TableCell>Requested by</TableCell><TableCell>Factor release</TableCell><TableCell>Snapshot</TableCell><TableCell>Status</TableCell><TableCell>Attempt</TableCell><TableCell>Issue</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
        <TableBody>{runs.map((run) => (
          <TableRow key={run.id}>
            <TableCell>{requestedAtLabel(run)}</TableCell>
            <TableCell>{run.requestedBy || 'Not recorded'}</TableCell>
            <TableCell>{releases.find((release) => release.id === run.factorReleaseId)?.version || run.factorReleaseVersion || 'Not available'} · {run.algorithmVersion || 'Not recorded'}</TableCell>
            <TableCell><abbr title={run.snapshotId}>{run.snapshotId ? run.snapshotId.slice(0, 8) : 'Not recorded'}</abbr></TableCell>
            <TableCell>
              {activeCalculationStatuses.has(run.status) ? (
                <>
                  <CalculationProgress status={{
                    label: calculationStatusLabel(run.status),
                    runStatus: run.status,
                    percent: run.progressPercent,
                  }} />
                  {run.currentModel ? (
                    <Typography color="textSecondary" variant="caption">
                      {String(run.currentModel).replace(/_/g, " ")}
                    </Typography>
                  ) : null}
                </>
              ) : (
                <CarbonStatus value={run.status} label={calculationStatusLabel(run.status)} />
              )}
            </TableCell>
            <TableCell>{run.attemptNumber ?? "—"}</TableCell>
            <TableCell>{run.failureCode ? friendlyCarbonError({ code: run.failureCode, message: run.failureCode }) : "No issue"}</TableCell>
            <TableCell align="right">
              {run.status === "succeeded" ? (
                <CarbonTableActions label={`Actions for calculation from ${requestedAtLabel(run)}`}>
                  <CarbonPageAction aria-label={`View calculation result from ${requestedAtLabel(run)}`} color="primary" size="small" to={`/carbonResults?revisionId=${encodeURIComponent(run.revisionId)}&runId=${encodeURIComponent(run.id)}`}>View results</CarbonPageAction>
                </CarbonTableActions>
              ) : null}
            </TableCell>
          </TableRow>
        ))}</TableBody>
      </CarbonDataTable>
    )}
  </CarbonSection>
);

export const CarbonCalculationViewer = ({ result }) => {
  if (!result) return <Typography color="textSecondary">Open a completed calculation to see its results.</Typography>;
  const totals = result.totals || {};
  return (
    <CarbonSection title="Calculation results">
      <Typography>Scope 1: {totals.scope1} tCO2e</Typography>
      <Typography>Scope 2 location-based: {totals.scope2Location} tCO2e</Typography>
      <Typography>Scope 2 market-based: {totals.scope2Market} tCO2e</Typography>
      <Typography>Outside scopes: {totals.outsideScopes} tCO2e</Typography>
      {(result.warnings || []).map((warning) => (
        <Typography role="alert" key={`${warning.code}-${warning.path || ""}`}>
          {warning.message || friendlyCarbonError(warning)}
        </Typography>
      ))}
      <CarbonDataTable label="Calculation lines">
        <TableHead><TableRow><TableCell>Category</TableCell><TableCell>Method</TableCell><TableCell>tCO2e</TableCell><TableCell>Emission factor used</TableCell></TableRow></TableHead>
        <TableBody>{(result.lines || []).map((line) => (
          <TableRow key={line.id}><TableCell>{String(line.category || "").replace(/_/g, " ")}</TableCell><TableCell>{String(line.method || "").replace(/_/g, " ")}</TableCell><TableCell>{line.valueTco2e}</TableCell><TableCell>{factorReasonLabel(line.factorSelectionReason)}</TableCell></TableRow>
        ))}</TableBody>
      </CarbonDataTable>
      <CarbonTechnicalDetails>
        <Typography variant="body2">Run ID: {result.runId}</Typography>
        <Typography variant="body2">Line reconciliation: {result.reconciliation?.lineTotal}</Typography>
        {(result.lines || []).map((line) => (
          <Typography key={`factor-${line.id}`} variant="body2">{line.category}: factor {line.factorId || "custom"} ({line.factorSelectionReason || "not recorded"})</Typography>
        ))}
      </CarbonTechnicalDetails>
    </CarbonSection>
  );
};

export const CarbonCalculationPage = ({ accessToken }) => {
  const tokenOps = useTokenOps();
  const contextToken = tokenOps.account?.accessToken;
  const token = accessToken || contextToken;
  const generation = useRef(0);
  const timer = useRef(null);
  const [inventories, setInventories] = useState([]);
  const [inventoryId, setInventoryId] = useState("");
  const [revisions, setRevisions] = useState([]);
  const [revisionId, setRevisionId] = useState("");
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState("");
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadHistory = async (id, current = generation.current) => {
    const page = await carbonRequest(token, `corporate-carbon/revisions/${id}/calculations?page=1&limit=100`);
    if (current !== generation.current) return;
    const rows = page.items || page.data || [];
    setRuns(rows);
    if (rows.some((run) => ["pending", "claimed", "running"].includes(run.status))) {
      timer.current = setTimeout(() => loadHistory(id, current).catch((requestError) => {
        if (current === generation.current) setError(friendlyCarbonError(requestError));
      }), 3000);
    }
  };

  useEffect(() => {
    const current = ++generation.current;
    clearTimeout(timer.current);
    setInventoryId(""); setRevisionId(""); setRuns([]); setError("");
    if (!token) return undefined;
    const requestedContext = carbonContextFromSearch(
      window.location.hash || window.location.search,
    );
    Promise.all([
      carbonRequest(token, "corporate-carbon/inventories"),
      carbonRequest(token, "corporate-carbon/factor-releases?status=published&limit=100"),
    ]).then(async ([inventoryRows, releaseRows]) => {
      if (current !== generation.current) return;
      setInventories(inventoryRows);
      const factorRows = releaseRows.data || releaseRows.items || releaseRows;
      setReleases(factorRows);
      setReleaseId(factorRows[0]?.id || "");
      if (!requestedContext.inventoryId) return;
      setInventoryId(requestedContext.inventoryId);
      const revisionRows = await carbonRequest(token, `corporate-carbon/inventories/${requestedContext.inventoryId}/revisions`);
      if (current !== generation.current) return;
      setRevisions(revisionRows);
      if (requestedContext.revisionId && revisionRows.some((row) => row.id === requestedContext.revisionId)) {
        setRevisionId(requestedContext.revisionId);
        await loadHistory(requestedContext.revisionId, current);
      }
    }).catch((requestError) => {
      if (current === generation.current) setError(friendlyCarbonError(requestError));
    });
    return () => { if (current === generation.current) generation.current += 1; clearTimeout(timer.current); };
  // loadHistory uses the current render's token and generation guard.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const chooseInventory = async (selectedInventoryId) => {
    const current = ++generation.current;
    clearTimeout(timer.current); setInventoryId(selectedInventoryId); setRevisionId(""); setRuns([]);
    if (!selectedInventoryId) return setRevisions([]);
    try {
      const rows = await carbonRequest(token, `corporate-carbon/inventories/${selectedInventoryId}/revisions`);
      if (current === generation.current) setRevisions(rows);
    } catch (requestError) { if (current === generation.current) setError(friendlyCarbonError(requestError)); }
  };
  const chooseRevision = async (id) => {
    const current = ++generation.current;
    clearTimeout(timer.current); setRevisionId(id); setError("");
    if (!id) return setRuns([]);
    try { await loadHistory(id, current); }
    catch (requestError) { if (current === generation.current) setError(friendlyCarbonError(requestError)); }
  };
  const calculate = async () => {
    if (!revisionId || !releaseId || busy) return;
    const current = generation.current;
    setBusy(true); setError("");
    try {
      await carbonRequest(token, `corporate-carbon/revisions/${revisionId}/calculations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ factorReleaseId: releaseId, algorithmVersion: "2.0.0" }),
      });
      await loadHistory(revisionId, current);
    } catch (requestError) { if (current === generation.current) setError(friendlyCarbonError(requestError)); }
    finally { if (current === generation.current) setBusy(false); }
  };
  const selectedRelease = releases.find((release) => release.id === releaseId);
  const selectedRevision = revisions.find((revision) => revision.id === revisionId);
  const availability = calculationAvailability(selectedRevision, runs, releaseId);
  const activeRun = runs.find((run) => activeCalculationStatuses.has(run.status));
  return (
    <CarbonAdminPage title="Carbon calculations" description="Choose a reporting year and revision, then calculate its Scope 1 and Scope 2 emissions.">
      {error && <Typography role="alert">{error}</Typography>}
      <CarbonSection title="Calculation setup" description="Select the reporting data to calculate with the approved factor release.">
        <CarbonForm aria-label="Calculation setup" onSubmit={(event) => { event.preventDefault(); calculate(); }}>
        <CarbonSelectInput choices={inventories} choiceText={(row) => String(row.reportingYear)} label="Reporting year" name="inventoryId" value={inventoryId} onChange={(event) => chooseInventory(event.target.value)} />
        <CarbonSelectInput choices={revisions} choiceText={reportingRevisionLabel} label="Reporting revision" name="revisionId" value={revisionId} onChange={(event) => chooseRevision(event.target.value)} />
        <Typography color="textSecondary" variant="body2">Using approved emission factors: {selectedRelease?.version || "none available"}</Typography>
        {releases.length > 1 && (
          <>
            <CarbonSelectInput choices={releases} choiceText={(row) => row.version || row.name || "Published factors"} emptyText={null} label="Emission factor version" name="releaseId" value={releaseId} onChange={(event) => setReleaseId(event.target.value)} />
          </>
        )}
        {revisionId && availability.reason && <Typography color="textSecondary" variant="body2">{availability.reason}</Typography>}
        {selectedRevision?.status === 'draft' && <CarbonPageAction to={`/carbonFinalization?inventoryId=${encodeURIComponent(inventoryId)}&revisionId=${encodeURIComponent(revisionId)}`}>Go to Finalization</CarbonPageAction>}
        <CarbonFormToolbar><CarbonCalculationControl activeRun={activeRun} busy={busy} disabled={!revisionId || !releaseId || !availability.allowed} /></CarbonFormToolbar>
        </CarbonForm>
      </CarbonSection>
      <CarbonCalculationRuns runs={runs} releases={releases} />
    </CarbonAdminPage>
  );
};

const carbonCalculationResource = () => <Resource name="carbonCalculations" options={{ label: "Calculations", menuGroup: "Carbon Accounting" }} list={CarbonCalculationPage} />;
export default carbonCalculationResource;
